// The /filter-view table's own logic, kept out of the component so it can be
// tested: what the Origin cell says, how search narrows, how sort orders.
//
// The page lists TWO KINDS of saved filter (see back/utils/savedFilterRows):
// timeline filters, scoped to one timeline, and company tracking rule sets,
// offered on every project. Telling them apart is not decoration - they are
// loaded and deleted in different places - so the Origin cell is written to
// be incapable of coming out blank.

import moment from "moment";

// What the cell says when a row arrives with no origin at all. It is never
// empty, so a row of unknown kind is visible AS unknown instead of looking
// like a row whose kind simply was not worth printing.
export const ORIGIN_UNKNOWN = "Unknown kind";

export const originText = (row) => {
  const o = row && row.origin;
  return typeof o === "string" && o.trim() ? o.trim() : ORIGIN_UNKNOWN;
};

export const scopeText = (row) => {
  const s = row && row.scope;
  return typeof s === "string" && s.trim() ? s.trim() : "";
};

// Every field a reader can reasonably search by, each guarded: a deleted
// project leaves projectTitle empty rather than throwing, which is what the
// old page did on projectId.title and took the whole list down with it.
const haystack = (row) =>
  [
    row && row.name,
    row && row.projectTitle,
    row && row.projectNumber,
    row && row.savedBy,
    originText(row),
  ]
    .filter((x) => typeof x === "string" && x)
    .join(" ")
    .toLowerCase();

export const matchesSearch = (row, keyword) => {
  const k = String(keyword || "").trim().toLowerCase();
  if (!k) return true;
  return haystack(row).includes(k);
};

// ALWAYS from the full list. The old page filtered the already-filtered
// list, so correcting a typo could never widen the result again - the rows
// were gone until the box was emptied.
export const searchRows = (allRows, keyword) =>
  (Array.isArray(allRows) ? allRows : []).filter((r) => matchesSearch(r, keyword));

const stamp = (row) => {
  const t = row && row.date ? Date.parse(row.date) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

// Undated rows sort last either way rather than pretending to be the oldest.
export const sortRows = (rows, newestFirst) => {
  const list = (Array.isArray(rows) ? rows : []).slice();
  list.sort((a, b) => {
    const ta = stamp(a);
    const tb = stamp(b);
    if (!ta && !tb) return 0;
    if (!ta) return 1;
    if (!tb) return -1;
    return newestFirst ? tb - ta : ta - tb;
  });
  return list;
};

// The columns, in order. The Origin column is one of these entries and the
// body renders from this list, so there is no second place where a column
// could be left out of the rows but kept in the header.
export const COLUMNS = [
  { key: "name", head: "Name", cell: (r) => r.name || "" },
  { key: "origin", head: "Origin", cell: originText },
  { key: "scope", head: "Applies to", cell: scopeText },
  { key: "project", head: "Project", cell: (r) => r.projectTitle || "" },
  { key: "savedBy", head: "Saved by", cell: (r) => r.savedBy || "" },
  { key: "rules", head: "Rules", cell: (r) => String(r.rules == null ? "" : r.rules) },
  { key: "date", head: "Saved", cell: (r) => dateText(r) },
];

export const dateText = (row) => {
  const d = row && row.date;
  if (!d) return "";
  const m = moment(d);
  return m.isValid() ? m.format("DD/MM/YYYY") : "";
};

// The page says what it READ, not only what it found. Both kinds are always
// counted, so a company with no tracking rule sets sees "0 tracking rules"
// rather than a list that looks as though the second kind does not exist.
export const summaryText = (counts) => {
  const c = counts || {};
  const t = Number(c.timeline) || 0;
  const r = Number(c.tracking) || 0;
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  return `${plural(t, "timeline filter", "timeline filters")} and ${plural(r, "company tracking rule set", "company tracking rule sets")}`;
};
